import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AccountDto,
  CompanyProfile,
  CompanySettingsResponse,
  FiscalPeriodDto,
} from '@hesabat/contracts';
import { api, ProblemError, UnauthorizedError } from './api';
import CompanyBranding from './CompanyBranding';
import JournalEntry from './JournalEntry';
import Loading from './Loading';
import Members from './Members';
import Periods from './Periods';
import Wizard, { useWizardDraft, type WizardStep } from './Wizard';
import Hint from './Hint';

interface Props {
  companyId: string;
  currency: string;
  currentUserId: string;
  accounts: readonly AccountDto[];
  periods: readonly FiscalPeriodDto[];
  canEditSettings: boolean;
  canManageUsers: boolean;
  canPostManual: boolean;
  canClosePeriods: boolean;
  canReopenPeriods: boolean;
  /** بعد حفظ يغيّر ما يقرؤه التطبيق (الاسم، الفترات، الميزان). */
  onChanged: () => void;
  onDone: () => void;
  onOpenLedger: (accountCode: string) => void;
  onSessionLost: () => void;
}

interface SetupDraft {
  step: number;
  done: string[];
  /** بيانات الشركة قبل حفظها — حقول عامة لا سر فيها. */
  profile: CompanyProfile | null;
}

const CLASS_LABELS: Record<AccountDto['accountClass'], string> = {
  asset: 'أصول',
  liability: 'خصوم',
  equity: 'حقوق ملكية',
  revenue: 'إيرادات',
  cogs: 'تكلفة المبيعات',
  expense: 'مصروفات',
  other: 'بنود أخرى',
};

export function setupDraftKey(companyId: string): string {
  return 'hesabat.setup.' + companyId;
}

export function setupDoneKey(companyId: string): string {
  return 'hesabat.setup-done.' + companyId;
}

/** رقم التسجيل الضريبى فارغ أو ٩ أرقام — نفس قيد العقد، لتنبيه المستخدم قبل الحفظ. */
export function validTaxNumber(value: string | null): boolean {
  return value === null || value === '' || /^\d{9}$/.test(value);
}

/**
 * إعداد الشركة لأول مرة: يجمع فى مسار واحد ما كان متفرقًا فى أربع شاشات. كل خطوة تستعمل
 * شاشتها الأصلية (الهوية، الفترات، قيد الافتتاح، المستخدمون) فلا منطق جديد هنا — بيانات
 * الشركة وحدها نموذج مختصر يحفظ بنفس مسار الإعدادات.
 */
export default function CompanySetup({
  companyId,
  currency,
  currentUserId,
  accounts,
  periods,
  canEditSettings,
  canManageUsers,
  canPostManual,
  canClosePeriods,
  canReopenPeriods,
  onChanged,
  onDone,
  onOpenLedger,
  onSessionLost,
}: Props) {
  const [draft, setDraft, clearDraft, savedAt] = useWizardDraft<SetupDraft>(
    setupDraftKey(companyId),
    { step: 0, done: [], profile: null },
  );
  const [loaded, setLoaded] = useState<CompanySettingsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const handleError = useCallback(
    (caught: unknown) => {
      if (caught instanceof UnauthorizedError) {
        onSessionLost();
        return;
      }
      if (caught instanceof ProblemError) {
        const issues = caught.problem.issues ?? [];
        setFailure(
          issues.length > 0
            ? issues.map((issue) => issue.message).join('، ')
            : (caught.problem.detail ?? caught.problem.title),
        );
        return;
      }
      setFailure(caught instanceof Error ? caught.message : String(caught));
    },
    [onSessionLost],
  );

  useEffect(() => {
    api.companySettings(companyId).then(setLoaded).catch(handleError);
  }, [companyId, handleError]);

  const steps = useMemo(() => {
    const all: (WizardStep & { show: boolean })[] = [
      {
        id: 'profile',
        title: 'بيانات الشركة',
        hint: 'الاسم والرقم الضريبى يظهران على كل فاتورة وتقرير.',
        icon: 'home',
        show: true,
      },
      {
        id: 'branding',
        title: 'الهوية واللوجو',
        hint: 'شعارك وألوانك على الفاتورة المطبوعة — يمكنك تأجيلها.',
        icon: 'sparkle',
        optional: true,
        show: true,
      },
      {
        id: 'fiscal',
        title: 'السنة المالية',
        hint: 'الفترات التى تُسجَّل فيها الحركة. تُقفل شهرًا بشهر بعد المراجعة.',
        icon: 'calendar',
        show: true,
      },
      {
        id: 'accounts',
        title: 'دليل الحسابات',
        hint: 'الدليل المصرى جاهز بحساباته — راجعه، ولا تحتاج تعديله للبدء.',
        icon: 'ledger',
        show: true,
      },
      {
        id: 'opening',
        title: 'الأرصدة الافتتاحية',
        hint: 'ما فى الخزينة والبنك وما لك وعليك يوم بدء العمل على البرنامج.',
        icon: 'journal',
        optional: true,
        show: canPostManual,
      },
      {
        id: 'users',
        title: 'المستخدمون',
        hint: 'أضف المحاسب أو من يُدخل البيانات، كلٌّ بدوره.',
        icon: 'users',
        optional: true,
        show: canManageUsers,
      },
    ];
    return all.filter((step) => step.show);
  }, [canPostManual, canManageUsers]);

  const completed = useMemo(() => new Set(draft.done), [draft.done]);
  const current = Math.min(draft.step, steps.length - 1);
  const step = steps[current]!;
  const profile = draft.profile ?? loaded?.profile ?? null;

  function markDone(id: string): void {
    setDraft((value) => ({
      ...value,
      done: value.done.includes(id) ? value.done : [...value.done, id],
    }));
  }

  function setProfile(change: Partial<CompanyProfile>): void {
    if (!profile) return;
    setFailure(null);
    setDraft((value) => ({ ...value, profile: { ...profile, ...change } }));
  }

  async function saveProfile(): Promise<boolean> {
    if (!loaded || !profile) return false;
    setBusy(true);
    setFailure(null);
    try {
      // الإعدادات الأخرى تُرسل كما جاءت — هذه الخطوة لا تغيّر خيارًا محاسبيًا
      const result = await api.updateCompanySettings(companyId, {
        profile,
        settings: loaded.settings,
      });
      setLoaded(result);
      setDraft((value) => ({ ...value, profile: null }));
      onChanged();
      return true;
    } catch (caught) {
      handleError(caught);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function next(): Promise<boolean> {
    if (step.id === 'profile' && canEditSettings && !(await saveProfile())) return false;
    markDone(step.id);
    return true;
  }

  if (!loaded || !profile) {
    return (
      <div className="panel">{failure ? <p className="error">{failure}</p> : <Loading />}</div>
    );
  }

  const nullable = (text: string) => (text.trim() === '' ? null : text);
  const profileReady =
    profile.legalNameAr.trim().length >= 2 && validTaxNumber(profile.taxRegistrationNumber);
  const byClass = Object.entries(
    accounts.reduce<Record<string, number>>((counts, account) => {
      if (account.isPostable)
        counts[account.accountClass] = (counts[account.accountClass] ?? 0) + 1;
      return counts;
    }, {}),
  );

  return (
    <Wizard
      title="إعداد شركتك"
      intro="خطوات قليلة وتبدأ — تقدّمك محفوظ، فاخرج وعد متى شئت."
      steps={steps}
      current={current}
      onStep={(index) => setDraft((value) => ({ ...value, step: index }))}
      completed={completed}
      canNext={step.id !== 'profile' || profileReady}
      onNext={next}
      onFinish={() => {
        clearDraft();
        try {
          window.localStorage.setItem(setupDoneKey(companyId), '1');
        } catch {
          // بلا تخزين تعود الدعوة فى الزيارة القادمة — لا ضرر
        }
        onDone();
      }}
      finishLabel="انتهيت — إلى الرئيسية"
      busy={busy}
      savedAt={savedAt}
    >
      {failure && <p className="error">{failure}</p>}

      {step.id === 'profile' && (
        <div className="form-row">
          <label className="grow">
            الاسم القانونى بالعربى
            <input
              value={profile.legalNameAr}
              maxLength={200}
              disabled={!canEditSettings}
              onChange={(event) => setProfile({ legalNameAr: event.target.value })}
            />
            <Hint>كما هو مكتوب فى السجل التجارى بالضبط؛ يظهر أعلى الفواتير.</Hint>
          </label>
          <label className="grow">
            الاسم بالإنجليزى
            <input
              dir="ltr"
              value={profile.legalNameEn ?? ''}
              maxLength={200}
              disabled={!canEditSettings}
              onChange={(event) => setProfile({ legalNameEn: nullable(event.target.value) })}
            />
            <Hint>اختيارى — يظهر فى المستندات بالإنجليزية.</Hint>
          </label>
          <label className="grow">
            رقم التسجيل الضريبى (٩ أرقام)
            <input
              dir="ltr"
              inputMode="numeric"
              value={profile.taxRegistrationNumber ?? ''}
              maxLength={9}
              disabled={!canEditSettings}
              aria-invalid={!validTaxNumber(profile.taxRegistrationNumber)}
              onChange={(event) =>
                setProfile({ taxRegistrationNumber: nullable(event.target.value) })
              }
            />
            <Hint>الرقم المكتوب فى البطاقة الضريبية، ٩ أرقام بلا شرطات.</Hint>
          </label>
          <label className="grow">
            رقم السجل التجارى
            <input
              dir="ltr"
              value={profile.commercialRegistryNumber ?? ''}
              maxLength={32}
              disabled={!canEditSettings}
              onChange={(event) =>
                setProfile({ commercialRegistryNumber: nullable(event.target.value) })
              }
            />
            <Hint>من شهادة السجل التجارى.</Hint>
          </label>
          <label>
            العملة
            <input value={loaded.functionalCurrency} disabled />
            <Hint>العملة التى تُسجَّل بها كل حسابات الشركة.</Hint>
          </label>
        </div>
      )}

      {step.id === 'branding' && (
        <CompanyBranding
          companyId={companyId}
          profile={loaded.profile}
          canEdit={canEditSettings}
          onSessionLost={onSessionLost}
        />
      )}

      {step.id === 'fiscal' && (
        <Periods
          companyId={companyId}
          periods={periods}
          canManage={canClosePeriods}
          canReopen={canReopenPeriods}
          onChanged={onChanged}
        />
      )}

      {step.id === 'accounts' && (
        <div className="setup-accounts">
          <p className="sub">
            {accounts.length} حسابًا فى الدليل، منها{' '}
            {accounts.filter((account) => account.isPostable).length} حسابًا تُسجَّل عليه الحركة.
          </p>
          <ul className="setup-classes">
            {byClass.map(([accountClass, count]) => (
              <li key={accountClass}>
                <b>{count}</b>
                <span>
                  {CLASS_LABELS[accountClass as AccountDto['accountClass']] ?? accountClass}
                </span>
              </li>
            ))}
          </ul>
          <p className="sub">
            تريد أن ترى حركة حساب بعينه؟{' '}
            <button
              type="button"
              className="link"
              onClick={() => {
                const first = accounts.find((account) => account.isPostable);
                if (first) onOpenLedger(first.code);
              }}
            >
              افتح كشف الحساب
            </button>
          </p>
        </div>
      )}

      {step.id === 'opening' && (
        <JournalEntry
          companyId={companyId}
          currency={currency}
          accounts={accounts}
          periods={periods}
          initialOpening
          onPosted={() => {
            markDone('opening');
            onChanged();
          }}
          onOpenLedger={onOpenLedger}
        />
      )}

      {step.id === 'users' && (
        <Members
          companyId={companyId}
          currentUserId={currentUserId}
          onSessionLost={onSessionLost}
        />
      )}
    </Wizard>
  );
}
